import React, { useEffect, useMemo, useState } from 'react';
import { Box, CircularProgress, IconButton, Tooltip, Typography } from '@mui/material';
import ChevronLeftRounded from '@mui/icons-material/ChevronLeftRounded';
import ChevronRightRounded from '@mui/icons-material/ChevronRightRounded';
import { DataTable, type DataTableColumn, type RowTone } from './DataTable';
import { formatNumberValue } from '../utils/format';
import { SortHeader } from './SortHeader';
import { InfoHint, type InfoHintProps } from './InfoHint';

export interface ResultColumn {
  key: string;
  /** Shown in full; long names wrap rather than being cut off. */
  label: string;
  /** Right-aligned with digits lined up. */
  numeric?: boolean;
  /** How a value reads in a cell: "1,813", "2.26%", "$1.2M". Plain text by default. */
  format?: (value: unknown) => string;
  /**
   * The cell drawn as something other than text — a status chip, say. Sorting
   * still uses the value (or `sortValue`); `format` stays the text a reader
   * would copy.
   */
  render?: (value: unknown) => React.ReactNode;
  /** What a value sorts by, when that isn't the value itself — a date's timestamp. */
  sortValue?: (value: unknown) => string | number | null;
  /** What the column holds, behind an info icon after its name. */
  info?: React.ReactNode;
  /** `warning` turns that icon yellow: the column is worked out in a way worth reading first. */
  infoTone?: InfoHintProps['tone'];
  /**
   * Stays in place while the rest scrolls sideways — the columns that say
   * what a row is. Only leading columns, and they need a `width`.
   */
  sticky?: boolean;
  /** Fixed width, px. */
  width?: number;
  /** Long values wrap onto more lines instead of widening the column. */
  wrap?: boolean;
}

export interface ResultTableProps {
  columns: ResultColumn[];
  /** The rows loaded so far — the first ones, in order — each an array in column order. */
  rows: unknown[][];
  /** How many rows the result has in all, when more exist than are loaded. */
  totalRows?: number;
  /** Rows per page. A result that fits on one page shows no paging at all. */
  pageSize?: number;
  /** Fetch one page of rows beyond those loaded, for results larger than their preview. */
  onLoadPage?: (page: number, pageSize: number) => Promise<unknown[][]>;
  /** Scroll vertically beyond this height, with the header pinned. */
  maxHeight?: number | string;
  /**
   * Take the height its container gives it: the rows scroll, the header and
   * the paging stay in view — for a result filling a pane.
   */
  fill?: boolean;
  ariaLabel: string;
  /** A coloured edge on rows that need attention, read from the row's cells in column order. */
  rowTone?: (cells: unknown[]) => RowTone | null;
  /**
   * A panel opened under a row — its evidence, a breakdown — or null for a
   * row with nothing open. The same under-row panel `DataTable` draws.
   */
  renderRowDetail?: (cells: unknown[]) => React.ReactNode | null;
}

type Row = { i: number; cells: unknown[] };
type Sort = { index: number; dir: 'asc' | 'desc' } | null;

const EMPTY = '—';
/**
 * A cell with no formatter of its own. Numbers are capped at two decimals —
 * a query result arrives with whatever precision the arithmetic produced, and
 * a column of 0.07139999999999999 is unreadable and no more true than 0.07.
 */
const plain = (value: unknown) => {
  if (value === null || value === undefined || value === '') return EMPTY;
  if (typeof value === 'number') return formatNumberValue(value);
  return String(value);
};

function compare(a: string | number, b: string | number): number {
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });
}

/**
 * Any tabular result — the rows a query returned — readable at a glance.
 *
 * - Numbers are right-aligned and lined up, formatted by the column.
 * - Column names are shown in full.
 * - Clicking a heading sorts by it; again reverses; a third time clears.
 *   Blanks stay last either way.
 * - Paging appears only when the rows don't fit on one page, and fetches
 *   pages from the source when the result is bigger than what's loaded.
 *   Sorting covers the rows loaded so far, and says so when there are more.
 *
 * For figures laid out by design — grouped rows, pinned name columns — use
 * `DataTable`, which this is built on.
 */
export function ResultTable({
  columns,
  rows,
  totalRows,
  pageSize = 50,
  onLoadPage,
  maxHeight,
  fill = false,
  ariaLabel,
  rowTone,
  renderRowDetail,
}: ResultTableProps) {
  // Rows by position; pages fetched later fill in their stretch.
  const [loaded, setLoaded] = useState<Array<unknown[] | undefined>>(rows);
  const [page, setPage] = useState(0);
  const [sort, setSort] = useState<Sort>(null);
  const [fetching, setFetching] = useState(false);

  useEffect(() => {
    setLoaded(rows);
    setPage(0);
  }, [rows]);

  // A new page starts at its own first row: the rows scroll inside the
  // table, and a page turned while scrolled down would open part-way through.
  const frameRef = React.useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    frameRef.current?.querySelectorAll<HTMLElement>('[data-overflowing]').forEach((el) => {
      el.scrollTop = 0;
    });
  }, [page]);

  const loadedRows = useMemo(
    () => loaded.flatMap((cells, i) => (cells ? [{ i, cells }] : [])) as Row[],
    [loaded],
  );

  const sortedRows = useMemo(() => {
    if (!sort) return null;
    const col = columns[sort.index];
    const key = (v: unknown): string | number | null => {
      if (col?.sortValue) return col.sortValue(v);
      if (v === null || v === undefined || v === '') return null;
      return typeof v === 'number' ? v : String(v);
    };
    return [...loadedRows].sort((a, b) => {
      const ka = key(a.cells[sort.index]);
      const kb = key(b.cells[sort.index]);
      if (ka === null || kb === null) return ka === kb ? a.i - b.i : ka === null ? 1 : -1;
      const c = compare(ka, kb);
      return (sort.dir === 'asc' ? c : -c) || a.i - b.i;
    });
  }, [loadedRows, sort, columns]);

  const all = Math.max(totalRows ?? loaded.length, loaded.length);
  const total = sortedRows ? sortedRows.length : all;
  const pages = Math.max(1, Math.ceil(total / pageSize));
  const start = page * pageSize;
  const end = Math.min(start + pageSize, total);

  const visible: Row[] = sortedRows
    ? sortedRows.slice(start, end)
    : Array.from({ length: Math.max(0, end - start) }, (_, k) => start + k)
        .flatMap((i) => (loaded[i] ? [{ i, cells: loaded[i] as unknown[] }] : []));

  const pageIsLoaded = (p: number) => {
    const last = Math.min((p + 1) * pageSize, all);
    for (let i = p * pageSize; i < last; i += 1) if (!loaded[i]) return false;
    return true;
  };

  const goTo = async (next: number) => {
    if (!sortedRows && onLoadPage && !pageIsLoaded(next)) {
      setFetching(true);
      try {
        const fetched = await onLoadPage(next, pageSize);
        setLoaded((prev) => {
          const copy = [...prev];
          fetched.forEach((cells, k) => {
            copy[next * pageSize + k] = cells;
          });
          return copy;
        });
      } finally {
        setFetching(false);
      }
    }
    setPage(next);
  };

  const cycleSort = (index: number) => {
    setPage(0);
    setSort((s) =>
      !s || s.index !== index ? { index, dir: 'asc' } : s.dir === 'asc' ? { index, dir: 'desc' } : null,
    );
  };

  const tableColumns: DataTableColumn<Row>[] = columns.map((col, index) => {
    const active = sort?.index === index ? sort.dir : null;
    return {
      key: col.key,
      numeric: col.numeric,
      minWidth: 96,
      sticky: col.sticky,
      width: col.width,
      wrap: col.wrap,
      name: col.label,
      header: col.info ? (
        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
          <SortHeader label={col.label} numeric={col.numeric} direction={active} onClick={() => cycleSort(index)} />
          <InfoHint title={col.info} label={`About ${col.label}`} tone={col.infoTone} />
        </Box>
      ) : (
        <SortHeader label={col.label} numeric={col.numeric} direction={active} onClick={() => cycleSort(index)} />
      ),
      render: (row) => (col.render ? col.render(row.cells[index]) : (col.format ?? plain)(row.cells[index])),
    };
  });

  const partialSort = sortedRows !== null && all > loadedRows.length;

  return (
    <Box ref={frameRef} sx={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0, ...(fill && { flex: '1 1 auto', minHeight: 0 }) }}>
      <DataTable
        columns={tableColumns}
        groups={[{ key: 'rows', title: '', rows: visible }]}
        getRowKey={(row) => String(row.i)}
        rowTone={rowTone ? (row) => rowTone(row.cells) : undefined}
        renderRowDetail={renderRowDetail ? (row) => renderRowDetail(row.cells) : undefined}
        collapsibleGroups={false}
        maxHeight={maxHeight}
        fill={fill}
        ariaLabel={ariaLabel}
        emptyMessage="This result has no rows."
      />
      {(pages > 1 || partialSort) && (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1, flexShrink: 0 }}>
          {partialSort && (
            <Typography variant="caption" sx={(t) => ({ mr: 'auto', color: t.tokens.text.tertiary })}>
              Sorted within the {loadedRows.length.toLocaleString()} rows loaded so far
            </Typography>
          )}
          {fetching && <CircularProgress size={14} aria-label="Loading rows" />}
          {pages > 1 && (
            <>
              <Typography variant="caption" sx={(t) => ({ color: t.tokens.text.secondary, fontVariantNumeric: 'tabular-nums' })}>
                {(start + 1).toLocaleString()}–{end.toLocaleString()} of {total.toLocaleString()}
              </Typography>
              <Tooltip title="Previous rows">
                <span style={{ display: 'inline-flex' }}>
                  <IconButton size="small" aria-label="Previous rows" disabled={page === 0 || fetching} onClick={() => void goTo(page - 1)}>
                    <ChevronLeftRounded fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Next rows">
                <span style={{ display: 'inline-flex' }}>
                  <IconButton size="small" aria-label="Next rows" disabled={page >= pages - 1 || fetching} onClick={() => void goTo(page + 1)}>
                    <ChevronRightRounded fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </>
          )}
        </Box>
      )}
    </Box>
  );
}
